// src/tracker/components/AccountBalancesList.js

import React, { useState, useEffect } from 'react';
import { List, ListItem, ListItemText, ListItemAvatar, Avatar, Typography, Divider, Box, IconButton, CircularProgress, Paper } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useConfirmation } from '../../context/ConfirmationContext';
import { useNotification } from '../../context/NotificationContext';



const getAccountIcon = (type) => {
    switch (type) {
        case 'Bank Account': return <AccountBalanceIcon />;
        case 'E-Wallet': return <AccountBalanceWalletIcon />;
        case 'Cash': return <AttachMoneyIcon />;
        default: return <AccountBalanceWalletIcon />;
    }
};

const AccountBalancesList = ({ accounts, loading, onEdit, onAccountChange }) => {
    const confirm = useConfirmation();
    const { showNotification } = useNotification();
    const phpSymbol = '₱';
    const hiddenValue = '******';
    
    // State to manage the visibility of balances, initialized from localStorage or defaulting to true.
    const [balancesVisible, setBalancesVisible] = useState(() => {
        const savedState = localStorage.getItem('balancesVisible');
        return savedState !== null ? JSON.parse(savedState) : true;
    });

    // Effect to save the visibility state to localStorage whenever it changes.
    useEffect(() => {
        localStorage.setItem('balancesVisible', JSON.stringify(balancesVisible));
    }, [balancesVisible]);

    const handleDelete = async (accountId, accountName) => {
        const isConfirmed = await confirm({
            title: 'Delete Account?',
            message: `Are you sure you want to delete the account "${accountName}"? This action cannot be undone.`,
        });

        if (isConfirmed) {
            try {
                const response = await fetch(`https://frek.cc/proxy/38.54.86.24:8001/api/accounts/${accountId}`, { method: 'DELETE' });
                if (response.ok) {
                    showNotification('Account deleted successfully', 'success');
                    onAccountChange(); // Refresh the list
                } else {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Failed to delete account.');
                }
            } catch (error) {
                console.error("Delete error:", error);
                showNotification(`Error: ${error.message}`, 'error');
            }
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!accounts || accounts.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '140px' }}>
                <Typography color="text.secondary">No accounts yet. Add one to start tracking.</Typography>
            </Box>
        );
    }

    return (
        <Paper elevation={0} sx={{ p: 2, backgroundColor: 'transparent', border: 'none' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                    <Typography variant="h6" component="h2">Accounts</Typography>
                    <Typography variant="caption" color="text.secondary">Balances and available liquidity</Typography>
                </Box>
                <IconButton onClick={() => setBalancesVisible(!balancesVisible)} aria-label="toggle balance visibility">
                    {balancesVisible ? <VisibilityOff /> : <Visibility />}
                </IconButton>
            </Box>
            <List sx={{ width: '100%', p: 0, display: 'grid', gap: 1 }}>
                {accounts.map((account, index) => (
                    <React.Fragment key={account._id}>
                        <ListItem
                            sx={{
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                            secondaryAction={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body1" fontWeight="bold" sx={{ minWidth: '90px', textAlign: 'right' }}>
                                        {balancesVisible
                                            ? `${account.currency === 'USD' ? '$' : phpSymbol}${account.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                            : hiddenValue
                                        }
                                    </Typography>
                                    <IconButton edge="end" aria-label="edit" onClick={() => onEdit(account)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(account._id, account.name)}><DeleteIcon fontSize="small" /></IconButton>
                                </Box>
                            }
                        >
                            <ListItemAvatar sx={{ minWidth: '40px' }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'white' }}>{getAccountIcon(account.type)}</Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={<Typography variant="body2" fontWeight={600}>{account.name}</Typography>}
                                secondary={account.type}
                            />
                        </ListItem>
                        {index < accounts.length - 1 && <Divider sx={{ borderColor: 'transparent' }} />}
                    </React.Fragment>
                ))}
            </List>
        </Paper>
    );
};

export default AccountBalancesList;

